import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuNavigation, LuShare2, LuChevronUp, LuChevronDown, LuArrowUpDown, LuEllipsisVertical, LuCalendarClock, LuFileText, LuX } from 'react-icons/lu';
import ActionSheet from '../ui/ActionSheet';
import ReprogramarSheet from '../ordenes/ReprogramarSheet';
import api from '../../services/api';
import { linkRuta } from './MapaLeaflet';
import OrdenarRestoSheet from '../ordenes/OrdenarRestoSheet';
import { getTodayISO } from '../../utils/dateUtils';

// 28-oct-2026: lo que todavía falta hacer (lo hecho o no atendido no va a Google Maps ni se reordena)
export const FALTA = (p) => !['COMPLETADA', 'NO_ATENDIDO', 'CANCELADA'].includes(p.estado);

// Agenda del Mapa (13-oct-2026): debajo del mapa, el recorrido de cada técnico como lista
// (en orden de hora) con "Abrir en Google Maps" (te dice cómo llegar, parada por parada) y
// "Compartir" (manda la lista y el link por WhatsApp o lo que tenga el celu).
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const fmtDia = (iso) => { const [a, m, d] = iso.split('-').map(Number); return `del ${DIAS[new Date(a, m - 1, d).getDay()]} ${d}/${m}`; };

function textoRecorrido(nombre, fecha, paradas, url) {
    const lineas = paradas.map((p, i) => `${i + 1}. ${p.hora ? `${p.hora} · ` : ''}${p.cliente || p.titulo || 'Visita'}${p.direccion ? ` · ${p.direccion}` : ''}`);
    return `Recorrido ${fmtDia(fecha)} · ${nombre}\n\n${lineas.join('\n')}${url ? `\n\nCómo llegar, en orden:\n${url}` : ''}`;
}

// Cómo quedó cada visita (26-oct-2026): el recorrido es lo agendado, no por dónde pasó el técnico
const ESTADO = { COMPLETADA: ['Hecha', 'text-[#16A34A] dark:text-[#4ADE80]'], NO_ATENDIDO: ['No fue', 'text-brand-red'],
    EN_CAMINO: ['En camino', 'text-[#2563EB]'], EN_SITIO: ['En el lugar', 'text-[#2563EB]'] };
function estadoDe(p, fecha) {
    if (ESTADO[p.estado]) return ESTADO[p.estado];
    const hoy = new Date(); const iso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    const fin = String(p.hora || '').match(/-(\d\d):(\d\d)$/);
    const paso = fecha < iso || (fecha === iso && fin && hoy.getHours() * 60 + hoy.getMinutes() > Number(fin[1]) * 60 + Number(fin[2]));
    return paso && p.estado === 'PENDIENTE' ? ['Sin hacer', 'text-[#A16207] dark:text-[#F0A500]'] : null;
}

async function compartir(texto) {
    if (navigator.share) {
        try { await navigator.share({ text: texto }); return; } catch (e) { if (e?.name === 'AbortError') return; }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
}

export default function RecorridosAgenda({ puntos, fecha, onElegir, onCambio, onVerVisita }) {
    // ⋯ de cada visita (13-oct-2026): cambiar día/hora/técnico, ver la ficha o sacarla del día
    const [menu, setMenu] = useState(null);            // punto
    const [confirmar, setConfirmar] = useState(false);
    const [reprogramando, setReprogramando] = useState(null);   // { orden, tecnicos }
    const abrirReprogramar = async (p) => {
        setMenu(null);
        try {
            const [o, t] = await Promise.all([api.get(`/ordenes/${p.ordenId}`), api.get('/ordenes/tecnicos')]);
            setReprogramando({ orden: o.data, tecnicos: t.data || [] });
        } catch { toast.error('No se pudo abrir la visita'); }
    };
    const cancelar = async (p) => {
        try {
            await api.patch(`/ordenes/${p.ordenId}/estado`, { estado: 'CANCELADA' });
            toast.success('Visita cancelada: salió del recorrido');
            setMenu(null); setConfirmar(false); onCambio && onCambio();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo cancelar'); }
    };
    // Cambiar el orden (13-oct-2026): ▲▼ y "Guardar". Las mismas horas se reparten en el orden nuevo.
    const [ordenando, setOrdenando] = useState(null);   // { tecnicoId, lista }
    const [resto, setResto] = useState(null);           // hoy: { tecnicoId, nombre } → Ordenar lo que falta
    const [guardando, setGuardando] = useState(false);
    const mover = (i, delta) => setOrdenando(o => { const l = [...o.lista]; [l[i], l[i + delta]] = [l[i + delta], l[i]]; return { ...o, lista: l }; });
    const guardar = async () => {
        setGuardando(true);
        try {
            const r = await api.post('/mapa/reordenar', { ordenIds: ordenando.lista.map(p => p.ordenId) });
            toast.success(r.data.cambiadas ? `Orden guardado: ${r.data.cambiadas} visita${r.data.cambiadas !== 1 ? 's' : ''} con franja nueva. Le avisamos al técnico.` : 'Orden guardado');
            setOrdenando(null); onCambio && onCambio();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar el orden'); }
        finally { setGuardando(false); }
    };
    const por = new Map();
    puntos.forEach(p => { if (!por.has(p.tecnicoId)) por.set(p.tecnicoId, []); por.get(p.tecnicoId).push(p); });
    if (!por.size) return null;

    return (
        <div className="space-y-3">
            {[...por.values()].map(ps0 => {
                const editando = ordenando?.tecnicoId === ps0[0].tecnicoId;
                const ps = editando ? ordenando.lista : ps0;
                const faltan = ps0.filter(FALTA);
                const esHoy = fecha === getTodayISO();
                // Otro día: a mano, todas sin arrancar. Hoy: "Ordenar lo que falta" (con lo hecho adentro también)
                const sePuede = esHoy ? faltan.filter(p => p.estado === 'PENDIENTE' && p.ordenId).length > 1
                    : ps0.length > 1 && ps0.every(p => p.estado === 'PENDIENTE' && p.ordenId);
                const nombre = String(ps[0].tecnico || 'Sin técnico').split(' ')[0];
                const url = linkRuta(editando ? ps : ps.filter(FALTA));
                const conHechas = faltan.length < ps0.length;
                return (
                    <div key={ps[0].tecnicoId} className="rounded-2xl bg-card border border-black/[0.07] dark:border-white/[0.07] overflow-hidden">
                        <div className="px-3.5 pt-3 pb-2 flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: (ps.find(p => !p.hecha) || ps[0]).color }} />
                            <p className="flex-1 text-body font-black text-ink">Recorrido de {nombre}</p>
                            <span className="text-caption text-muted">
                                {ps.length} visita{ps.length !== 1 ? 's' : ''}
                                {ps.some(p => estadoDe(p, fecha)) ? ` · ${ps.filter(p => p.estado === 'COMPLETADA').length} hecha${ps.filter(p => p.estado === 'COMPLETADA').length !== 1 ? 's' : ''}` : ''}
                            </span>
                            {sePuede && !editando && (
                                <button type="button" onClick={() => (esHoy ? setResto({ tecnicoId: ps0[0].tecnicoId, nombre: ps0[0].tecnico || 'Técnico' })
                                    : setOrdenando({ tecnicoId: ps0[0].tecnicoId, lista: [...ps0] }))}
                                    className="h-8 px-2.5 rounded-lg bg-chip text-label font-black text-secondary inline-flex items-center gap-1"><LuArrowUpDown size={13} /> {esHoy ? 'Ordenar lo que falta' : 'Cambiar orden'}</button>
                            )}
                        </div>
                        <ol>
                            {ps.map((p, i) => (
                                <li key={p.id} className="flex items-center">
                                    <button type="button" onClick={() => (p.lat != null ? onElegir(p) : toast('Esta visita no está ubicada en el mapa'))}
                                        className="flex-1 min-w-0 flex items-center gap-2.5 px-3.5 py-2 text-left border-t border-black/[0.05] dark:border-white/[0.05] active:bg-chip">
                                        <span className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-label font-black text-white" style={{ background: p.color || ps[0].color }}>{editando ? i + 1 : (p.etiqueta || i + 1)}</span>
                                        <span className="flex-1 min-w-0">
                                            <span className={`block text-body font-black truncate ${p.hecha ? 'text-muted' : 'text-ink'}`}>{p.cliente || p.titulo}</span>
                                            <span className="block text-label text-muted truncate">{p.lat == null ? 'Sin ubicar · ' : ''}{p.direccion || 'Sin dirección'}</span>
                                        </span>
                                        {!editando && (
                                            <span className="shrink-0 text-right">
                                                <span className="block text-caption font-black text-ink">{p.hecha && p.completada ? String(p.completada).slice(11, 16) : p.hora ? p.hora.replace(/^(\d{2}):00-(\d{2}):00$/, (_, a, b) => `${Number(a)} a ${Number(b)}`) : 'Sin hora'}</span>
                                                {estadoDe(p, fecha) && <span className={`block text-label font-black ${estadoDe(p, fecha)[1]}`}>{estadoDe(p, fecha)[0]}</span>}
                                            </span>
                                        )}
                                    </button>
                                    {!editando && p.ordenId && (
                                        <button type="button" onClick={() => { setConfirmar(false); setMenu(p); }} aria-label="Opciones de la visita"
                                            className="w-9 h-9 mr-2 shrink-0 rounded-lg text-muted flex items-center justify-center active:bg-chip"><LuEllipsisVertical size={17} /></button>
                                    )}
                                    {editando && (
                                        <span className="flex gap-1 pr-3">
                                            <button type="button" onClick={() => mover(i, -1)} disabled={i === 0} aria-label="Subir" className="w-9 h-9 rounded-lg bg-chip text-secondary flex items-center justify-center disabled:opacity-30"><LuChevronUp size={16} /></button>
                                            <button type="button" onClick={() => mover(i, 1)} disabled={i === ps.length - 1} aria-label="Bajar" className="w-9 h-9 rounded-lg bg-chip text-secondary flex items-center justify-center disabled:opacity-30"><LuChevronDown size={16} /></button>
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ol>
                        {editando ? (
                        <div className="p-3 space-y-2 border-t border-black/[0.06] dark:border-white/[0.06]">
                            <p className="text-caption text-muted">{ps0.some(p => !p.hora) ? 'Las que no tienen hora toman la franja según el recorrido, saliendo de la base.' : `Las horas se reparten en el orden nuevo (${ps0.map(p => p.hora).join(' → ')}).`} Al técnico le llega el aviso y vuelve a dar "Ok, voy".</p>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setOrdenando(null)} className="flex-1 h-11 rounded-xl bg-chip text-secondary text-label font-black">Cancelar</button>
                                <button type="button" onClick={guardar} disabled={guardando} className="flex-[2] h-11 rounded-xl bg-brand-red text-white text-label font-black disabled:opacity-50">{guardando ? 'Guardando…' : 'Guardar orden'}</button>
                            </div>
                        </div>
                        ) : (
                        <div className="p-3 flex gap-2 border-t border-black/[0.06] dark:border-white/[0.06]">
                            {url && (
                                <a href={url} target="_blank" rel="noreferrer"
                                    className="flex-[2] h-11 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                    <LuNavigation size={15} /> {conHechas ? `Google Maps (${faltan.length} que falta${faltan.length !== 1 ? 'n' : ''})` : 'Abrir en Google Maps'}
                                </a>
                            )}
                            <button type="button" onClick={() => compartir(textoRecorrido(nombre, fecha, ps, url))}
                                className="flex-1 h-11 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                                <LuShare2 size={15} /> Compartir
                            </button>
                        </div>
                        )}
                    </div>
                );
            })}
            <p className="text-caption text-muted px-1">Google Maps arma el camino desde donde estés, pasando por las visitas que faltan en este orden.</p>
            {resto && <OrdenarRestoSheet tecnicoId={resto.tecnicoId} tecnicoNombre={resto.nombre} fecha={fecha}
                onCerrar={() => setResto(null)} onListo={() => { setResto(null); onCambio && onCambio(); }} />}
            <ActionSheet open={!!menu} onClose={() => { setMenu(null); setConfirmar(false); }}>
                {menu && (() => {
                    const item = 'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-body font-bold text-ink active:bg-chip text-left';
                    const arrancada = menu.estado && menu.estado !== 'PENDIENTE';
                    return (
                        <>
                            <p className="px-4 pt-1 pb-2 text-label font-black uppercase tracking-widest text-muted truncate">{menu.cliente || menu.titulo}{menu.hora ? ` · ${menu.hora}` : ''}</p>
                            {!arrancada && <button className={item} onClick={() => abrirReprogramar(menu)}><LuCalendarClock size={18} /> Cambiar día, hora o técnico</button>}
                            {onVerVisita && <button className={item} onClick={() => { const id = menu.ordenId; setMenu(null); onVerVisita(id); }}><LuFileText size={18} /> Ver ficha de la visita</button>}
                            {!arrancada && (confirmar ? (
                                <div className="mx-2 mt-1 p-3 rounded-xl bg-[color:var(--alerta-bg)] space-y-2">
                                    <p className="text-caption font-bold text-[color:var(--alerta-tx)]">¿Cancelar esta visita? Sale del recorrido y al técnico le llega el aviso.</p>
                                    <div className="flex gap-2">
                                        <button onClick={() => setConfirmar(false)} className="flex-1 h-10 rounded-xl bg-chip text-secondary text-label font-black">No</button>
                                        <button onClick={() => cancelar(menu)} className="flex-1 h-10 rounded-xl bg-brand-red text-white text-label font-black">Sí, cancelar</button>
                                    </div>
                                </div>
                            ) : (
                                <button className={`${item} text-brand-red`} onClick={() => setConfirmar(true)}><LuX size={18} /> Cancelar visita</button>
                            ))}
                            {arrancada && <p className="px-4 py-2 text-caption text-muted">El técnico ya salió o está en el lugar: para cambiarla, abrí la ficha.</p>}
                        </>
                    );
                })()}
            </ActionSheet>
            {reprogramando && (
                <ReprogramarSheet orden={reprogramando.orden} tecnicos={reprogramando.tecnicos}
                    onCerrar={() => setReprogramando(null)}
                    onGuardado={() => { setReprogramando(null); onCambio && onCambio(); }} />
            )}
        </div>
    );
}
